// src/budget/model/columns/columnBuilders.js

import React from "react";
import { formatCurrency, formatPercent } from "../../../shared/utils/formatters.js";
import { formatFiscalYearFromStart } from "../../../shared/utils/fiscalYearUtils.js";

/**
 * Town data is now keyed by START YEAR.
 * startYear=2025 => FY 2025-2026
 */
function formatFiscalYearFromStartYear(startYear) {
  if (!Number.isFinite(startYear)) return "";
  // Prefer your shared utility if it returns "2025-2026"
  return formatFiscalYearFromStart(startYear);
}

/**
 * Percent formatter used by the BoE "Build a Budget" workshop tables.
 *
 * IMPORTANT:
 * - Expects a ratio (delta / baseline), not a "percent points" value.
 *   Examples:
 *     - 0.0681  =>  6.81%
 *     - 6.806   =>  680.60%
 *
 * We use this instead of the shared `formatPercent()` because the shared
 * formatter is used in other parts of the app and may assume different input
 * scaling (e.g., percent points vs ratio).
 *
 * @type {Intl.NumberFormat}
 */
const fmtPct2 = new Intl.NumberFormat("en-US", {
  style: "percent",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Builds an array of column definitions for rendering revenue tables.
 *
 * IMPORTANT:
 * - latestYear is the FY START year (Town start-year keyed).
 *
 * Ordering (Town standard):
 *   Y-2 Approved, Y-2 Actual, Y-1 Approved, Y Approved/Proposed
 *
 * @param {number} latestYear  FY START year (e.g., 2025 means FY 2025-2026)
 * @param {{ y0Status?: "Approved"|"Proposed" }} [opts]
 * @returns {{ columns: Array<Object>, baseColumns: Array<Object> }}
 */
export function buildRevenueColumns(latestYear, opts = {}) {
  const y0Status = opts?.y0Status === "Proposed" ? "Proposed" : "Approved";
  const y0Key = y0Status === "Proposed" ? "y0_proposed" : "y0_adopted";

  const renderCurrencyCell = (val) => {
    if (typeof val !== "number" || !Number.isFinite(val)) {
      return <div className="text-right">—</div>;
    }

    const isNegative = val < 0;

    return (
      <div className={`text-right ${isNegative ? "text-red-600" : ""}`}>
        {formatCurrency(val)}
      </div>
    );
  };

  const baseColumns = [
    {
      label: "",
      key: "label",
      style: { width: "300px", minWidth: "300px" },
    },
    {
      label: `${formatFiscalYearFromStartYear(latestYear - 2)} Approved`,
      year: latestYear - 2, // START year for that FY
      type: "adopted",
      key: "y2_adopted",
      editable: true,
      style: { width: "100px", minWidth: "100px" },
      render: (row) => renderCurrencyCell(row.y2_adopted),
    },
    {
      label: `${formatFiscalYearFromStartYear(latestYear - 2)} Actual`,
      year: latestYear - 2,
      type: "actual",
      key: "y2_actual",
      editable: true,
      style: { width: "100px", minWidth: "100px" },
      render: (row) => renderCurrencyCell(row.y2_actual),
    },
    {
      label: `${formatFiscalYearFromStartYear(latestYear - 1)} Approved`,
      year: latestYear - 1,
      type: "adopted",
      key: "y1_adopted",
      editable: true,
      style: { width: "100px", minWidth: "100px" },
      render: (row) => renderCurrencyCell(row.y1_adopted),
    },
    {
      label: `${formatFiscalYearFromStartYear(latestYear)} ${y0Status}`,
      year: latestYear,
      type: y0Status === "Proposed" ? "proposed" : "adopted",
      key: y0Key,
      editable: true,
      style: { width: "100px", minWidth: "100px" },
      render: (row) => renderCurrencyCell(row?.[y0Key]),
    },
  ];

  const derivedColumns = [
    {
      label: "Change",
      key: "increase",
      style: { width: "100px", minWidth: "100px" },
      editable: false,
      render: (row) => {
        const val = row.increase;
        if (val == null || val === 0) return <div className="text-right">—</div>;
        const isNegative = val < 0;
        return (
          <div className={`text-right ${isNegative ? "text-red-600" : ""}`}>
            {formatCurrency(val)}
          </div>
        );
      },
    },
    {
      label: "% Change",
      key: "percentIncrease",
      style: { width: "100px", minWidth: "100px" },
      editable: false,
      render: (row) => {
        const val = row.percentIncrease;
        if (val == null || val === 0) return <div className="text-right">—</div>;
        const isNegative = val < 0;
        return (
          <div className={`text-right ${isNegative ? "text-red-600" : ""}`}>
            {formatPercent(val)}
          </div>
        );
      },
    },
  ];

  return {
    columns: [...baseColumns, ...derivedColumns],
    baseColumns,
  };
}

/**
 * Builds explicitly defined projection columns for fund balance and operational result tables.
 *
 * @param {number} currentYear
 * @returns {{ columns: Array<Object>, baseColumns: Array<Object> }}
 */
export function buildProjectionColumns(currentYear) {
  const makeCellRenderer = (key) => (row) => {
    const val = row[key];

    if (row.isAnnotationRow || row.isFooterRow) {
      return (
        <div className="text-center text-xs italic">
          {typeof val === "string" && val.trim() !== "" ? val : " "}
        </div>
      );
    }

    const label = row.label?.trim();
    const isBold = label === "Expenditure Actuals & Estimates";
    const isPercent = label === "UFB as % of Expenditures";
    const isNegative = typeof val === "number" && val < 0;

    const numericClassNames = `${isPercent ? "text-center" : "text-right"} ${
      isBold ? "font-bold" : ""
    } ${isNegative ? "text-red-600" : ""}`;

    if (typeof val !== "number" || !Number.isFinite(val)) {
      const dashClassNames = `text-center ${isBold ? "font-bold" : ""}`;
      return <div className={dashClassNames}>—</div>;
    }

    const formatted = isPercent
      ? formatPercent(val)
      : val < 0
      ? `(${formatCurrency(Math.abs(val))})`
      : formatCurrency(val);

    return <div className={numericClassNames}>{formatted}</div>;
  };

  const actualStart = currentYear - 3;
  const actualEnd = currentYear - 2;
  const actualKey = `${actualStart}_${actualEnd}`;

  const projectedStarts = [
    currentYear - 2,
    currentYear - 1,
    currentYear,
    currentYear + 1,
    currentYear + 2,
  ];

  const projectedColumns = projectedStarts.map((fyStart) => {
    const fyEnd = fyStart + 1;
    const key = `${fyStart}_${fyEnd}`;

    return {
      label: (
        <div className="w-full" style={{ textAlign: "center" }}>
          {`FY${String(fyStart).slice(2)}/${String(fyEnd).slice(2)}`}
          <br />
          Projected
        </div>
      ),
      year: fyStart,
      type: "projected",
      key,
      style: { width: "100px", minWidth: "100px" },
      render: makeCellRenderer(key),
    };
  });

  const baseColumns = [
    {
      label: "",
      key: "label",
      style: { width: "240px", minWidth: "240px" },
    },
    {
      label: (
        <div className="w-full" style={{ textAlign: "center" }}>
          {`${actualStart}/${actualEnd}`}
          <br />
          Actuals
        </div>
      ),
      year: actualStart,
      type: "actual",
      key: actualKey,
      style: { width: "120px", minWidth: "120px" },
      render: makeCellRenderer(actualKey),
    },
    ...projectedColumns,
  ];

  return {
    columns: baseColumns,
    baseColumns,
  };
}

/**
 * Build columns for the "Build a Budget" workshop modal.
 *
 * Town is START-year keyed:
 * latestYear=2025 => FY 2025-2026
 *
 * Display columns (matches Snapshot style):
 * 1) (LatestYear - 2) Actual
 * 2) (LatestYear - 1) Approved
 * 3) (LatestYear) Approved/Proposed  (reference / baseline)
 * 4) Build a Budget                  (editable "latest" value)
 * 5) Change                          (derived)
 * 6) % Change                        (derived)
 *
 * Also includes a hidden fetch-only column for y0_proposed so workshop utils can reference it.
 */
export function buildBuildBudgetColumns(latestYear, opts = {}) {
  const y0Status = opts?.y0Status === "Proposed" ? "Proposed" : "Approved";
  const y0Key = y0Status === "Proposed" ? "y0_proposed" : "y0_adopted";

  const header2Line = (fyStart, line2) => (
    <div className="w-full text-center leading-tight">
      {formatFiscalYearFromStart(fyStart)}
      <br />
      {line2}
    </div>
  );

  const headerBottomAligned = (text) => (
    <div className="w-full text-center leading-tight">
      <span className="invisible">.</span>
      <br />
      {text}
    </div>
  );

  const renderCurrencyCell = (val) => {
    if (typeof val !== "number" || !Number.isFinite(val)) {
      return <div className="text-right">—</div>;
    }
    const isNegative = val < 0;
    return (
      <div className={`text-right ${isNegative ? "text-red-600" : ""}`}>
        {formatCurrency(val)}
      </div>
    );
  };

const renderPercentCell = (val) => {
  if (typeof val !== "number" || !Number.isFinite(val)) {
    return <div className="text-right">—</div>;
  }
  const isNegative = val < 0;
  return (
    <div className={`text-right ${isNegative ? "text-red-600" : ""}`}>
      {fmtPct2.format(val)}
    </div>
  );
};

  const displayBaseColumns = [
    {
      label: "",
      key: "label",
      style: { width: "330px", minWidth: "330px" },
      align: "left",
      headerAlign: "left",
    },
    {
      label: header2Line(latestYear - 2, "Actual"),
      year: latestYear - 2,
      type: "actual",
      key: "y2_actual",
      editable: false,
      format: "currency",
      align: "right",
      headerAlign: "center",
      style: { width: "120px", minWidth: "120px" },
      render: (row) => renderCurrencyCell(row.y2_actual),
    },
    {
      label: header2Line(latestYear - 1, "Approved"),
      year: latestYear - 1,
      type: "adopted",
      key: "y1_adopted",
      editable: false,
      format: "currency",
      align: "right",
      headerAlign: "center",
      style: { width: "120px", minWidth: "120px" },
      render: (row) => renderCurrencyCell(row.y1_adopted),
    },
    {
      label: header2Line(latestYear, y0Status),
      year: latestYear,
      type: y0Status === "Proposed" ? "proposed" : "adopted",
      key: y0Key,
      editable: false,
      format: "currency",
      align: "right",
      headerAlign: "center",
      style: { width: "120px", minWidth: "120px" },
      render: (row) => renderCurrencyCell(row?.[y0Key]),
    },
  ];

  const workshopColumns = [
    {
      label: headerBottomAligned("Build a Budget"),
      key: "buildBudget",
      editable: true,
      format: "currency",
      align: "right",
      headerAlign: "center",
      style: { width: "150px", minWidth: "150px" },
      render: (row) => renderCurrencyCell(row.buildBudget),
    },
    {
      label: headerBottomAligned("Change"),
      key: "increase",
      editable: false,
      format: "currency",
      align: "right",
      headerAlign: "center",
      style: { width: "120px", minWidth: "120px" },
      render: (row) => renderCurrencyCell(row.increase),
    },
    {
      label: headerBottomAligned("% Change"),
      key: "percentIncrease",
      editable: false,
      format: "percent",
      align: "right",
      headerAlign: "center",
      style: { width: "90px", minWidth: "90px" },
      render: (row) => renderPercentCell(row.percentIncrease),
    },
  ];

  // Fetch columns (includes hidden y0_proposed so you can always compute correctly)
  const baseColumns = [
    ...displayBaseColumns,
    {
      label: header2Line(latestYear, "Proposed"),
      year: latestYear,
      type: "proposed",
      key: "y0_proposed",
      editable: false,
      format: "currency",
      align: "right",
      headerAlign: "center",
      style: { width: "1px", minWidth: "1px" },
      render: () => null,
    },
  ];

  return {
    baseColumns,
    columns: [...displayBaseColumns, ...workshopColumns],
  };
}

/**
 * Town's "Build a Budget" column look/feel, but driven by a meta window
 * (BoE uses colKey values like "y2024_actual" etc.).
 */
export function buildBuildBudgetColumnsFromMeta(windowYearsMeta, options = {}) {
  const {
    labelColWidth = "300px",
    yearColWidth = "120px",
    buildBudgetColWidth = "150px",
    deltaColWidth = "120px",

    highlightColKey = null,
    buildBudgetHeaderBg = "bg-[#d5e7f7]",
  } = options;

  const buildTwoLineHeader = (fyLabel, sectionLabel) => (
    <div className="leading-tight text-center whitespace-nowrap">
      <div className="font-semibold">{fyLabel}</div>
      <div>{sectionLabel}</div>
    </div>
  );

  if (!Array.isArray(windowYearsMeta) || windowYearsMeta.length === 0) {
    return { columns: [] };
  }

  const latestMeta = windowYearsMeta[windowYearsMeta.length - 1];

  const cols = [
    {
      key: "label",
      label: "",
      editable: false,
      style: { width: labelColWidth, minWidth: labelColWidth },
      headerClassName: "bg-white",
      cellClassName: "truncate",
    },
  ];

  windowYearsMeta.forEach(({ year, sectionKey, colKey }) => {
    if (colKey === latestMeta.colKey) return;

    const fy = formatFiscalYearFromStart(year);

    const sectionLabel =
      sectionKey === "Approved"
        ? "Approved"
        : sectionKey === "Proposed"
        ? "Proposed"
        : "Actual";

    const isHighlighted = highlightColKey && colKey === highlightColKey;

    cols.push({
      key: colKey,
      label: buildTwoLineHeader(fy, sectionLabel),
      format: "currency",
      align: "right",
      editable: false,
      style: { width: yearColWidth, minWidth: yearColWidth },
      headerClassName: isHighlighted ? "bg-yellow-100" : undefined,
      cellClassName: isHighlighted ? "bg-yellow-50" : undefined,
    });
  });

  cols.push({
    key: "buildBudget",
    label: (
      <div className="leading-tight text-center whitespace-nowrap">
        <div className="font-semibold">Build a Budget</div>
        <div>{formatFiscalYearFromStart(latestMeta.year)}</div>
      </div>
    ),
    format: "currency",
    align: "right",
    editable: true,
    style: { width: buildBudgetColWidth, minWidth: buildBudgetColWidth },
    headerClassName: buildBudgetHeaderBg,
    cellClassName: "bg-white",
  });

  const headerBottomAligned = (text) => (
    <div className="w-full text-center leading-tight">
      <span className="invisible">.</span>
      <br />
      {text}
    </div>
  );

  const renderCurrencyCell = (val) => {
    if (typeof val !== "number" || !Number.isFinite(val)) {
      return <div className="text-right">—</div>;
    }
    const isNegative = val < 0;
    return (
      <div className={`text-right ${isNegative ? "text-red-600" : ""}`}>
        {formatCurrency(val)}
      </div>
    );
  };

const renderPercentCell = (val) => {
  if (typeof val !== "number" || !Number.isFinite(val)) {
    return <div className="text-right">—</div>;
  }
  const isNegative = val < 0;
  return (
    <div className={`text-right ${isNegative ? "text-red-600" : ""}`}>
      {fmtPct2.format(val)}
    </div>
  );
};

  cols.push(
    {
      key: "increase",
      label: headerBottomAligned("Change"),
      editable: false,
      format: "currency",
      align: "right",
      headerAlign: "center",
      style: { width: deltaColWidth, minWidth: deltaColWidth },
      render: (row) => renderCurrencyCell(row?.increase),
    },
    {
      key: "percentIncrease",
      label: headerBottomAligned("% Change"),
      editable: false,
      format: "percent",
      align: "right",
      headerAlign: "center",
      style: { width: deltaColWidth, minWidth: deltaColWidth },
      render: (row) => renderPercentCell(row?.percentIncrease),
    }
  );

  return { columns: cols };
}
